import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import { useAlert } from 'dashboard/composables';

const CSV_EXPORT_MAX_POLLS = 180;
const CSV_EXPORT_POLL_MS = 1000;
const CSV_EXPORT_ALERT_DURATION = 8000;
const CSV_EXPORT_TIMEOUT_MESSAGE = 'export timeout';

export const downloadCsvFile = (fileName, content) => {
  const contentType = 'data:text/csv;charset=utf-8;';
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.setAttribute('download', fileName);
  link.setAttribute('href', url);
  link.click();
  return link;
};

export const generateFileName = ({ type, to, businessHours = false }) => {
  let name = `${type}-report-${format(fromUnixTime(to), 'dd-MM-yyyy')}`;
  if (businessHours) {
    name = `${name}-business-hours`;
  }
  return `${name}.csv`;
};

export const STATISTICS_CSV_SECTION = {
  overview: 'overview',
  chats: 'chats',
  agent: 'agents',
  agents: 'agents',
  label: 'tags',
  tags: 'tags',
  inbox: 'sources',
  sources: 'sources',
  team: 'teams',
  teams: 'teams',
  bot: 'bot',
  queue: 'queue',
  agent_activity: 'activity',
  activity: 'activity',
  csat: 'csat',
};

// TZ §16: statistics_<section>_<YYYYMMDD>_<YYYYMMDD>_utc.csv
export const generateStatisticsFileName = ({ section, type, from, to }) => {
  const mapped =
    STATISTICS_CSV_SECTION[section] ||
    STATISTICS_CSV_SECTION[type] ||
    section ||
    type;
  const startDate = fromUnixTime(from);
  const endDate = fromUnixTime(to);
  const start = [
    startDate.getUTCFullYear(),
    String(startDate.getUTCMonth() + 1).padStart(2, '0'),
    String(startDate.getUTCDate()).padStart(2, '0'),
  ].join('');
  const end = [
    endDate.getUTCFullYear(),
    String(endDate.getUTCMonth() + 1).padStart(2, '0'),
    String(endDate.getUTCDate()).padStart(2, '0'),
  ].join('');
  return `statistics_${mapped}_${start}_${end}_utc.csv`;
};

const sleep = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

const blobToText = blob => {
  if (typeof blob === 'string') return Promise.resolve(blob);
  if (blob?.text) return blob.text();
  return Promise.resolve(String(blob || ''));
};

const parseExportPayload = async data => {
  try {
    return JSON.parse(await blobToText(data));
  } catch {
    return {};
  }
};

const extractCsvExportError = async error => {
  const data = error?.response?.data;
  if (data && typeof data === 'object' && typeof data.text !== 'function') {
    const message = data.error || data.message;
    if (message) return message;
    if (data.status === 'failed') return 'export failed';
  }

  const payload = await parseExportPayload(data);
  if (payload.status === 'failed' || payload.error) {
    return payload.error || 'export failed';
  }
  return error?.message || '';
};

const isGenericExportFailure = reason =>
  !reason ||
  reason === 'missing export id' ||
  reason === 'export failed' ||
  /^Request failed with status code \d+$/.test(reason);

const notifyCsvExportError = async error => {
  const reason = await extractCsvExportError(error);
  if (reason === CSV_EXPORT_TIMEOUT_MESSAGE) {
    useAlert('REPORT.DOWNLOAD_TIMEOUT', {
      usei18n: true,
      duration: CSV_EXPORT_ALERT_DURATION,
    });
    return;
  }
  if (!isGenericExportFailure(reason)) {
    useAlert(reason, { duration: CSV_EXPORT_ALERT_DURATION });
    return;
  }
  useAlert('REPORT.DOWNLOAD_FAILED', {
    usei18n: true,
    duration: CSV_EXPORT_ALERT_DURATION,
  });
};

const pollCsvExport = async (getExport, id, remaining) => {
  let response;
  try {
    response = await getExport(id);
  } catch (error) {
    const reason = await extractCsvExportError(error);
    throw new Error(reason || 'export failed');
  }

  const contentType = response.headers?.['content-type'] || '';
  if (response.status === 200 && contentType.includes('csv')) {
    return blobToText(response.data);
  }

  const payload = await parseExportPayload(response.data);
  if (payload.status === 'failed') {
    throw new Error(payload.error || 'export failed');
  }
  if (remaining <= 1) {
    throw new Error(CSV_EXPORT_TIMEOUT_MESSAGE);
  }

  await sleep(CSV_EXPORT_POLL_MS);
  return pollCsvExport(getExport, id, remaining - 1);
};

export const downloadStatisticsCsvAsync = async ({
  fileName,
  createExport,
  getExport,
}) => {
  try {
    const created = await createExport();
    const id = created?.data?.id;
    if (!id) throw new Error('missing export id');

    const text = await pollCsvExport(getExport, id, CSV_EXPORT_MAX_POLLS);
    downloadCsvFile(fileName, text);
  } catch (error) {
    await notifyCsvExportError(error);
    throw error;
  }
};
